import React, { useEffect, useState } from "react";
import { plans as plansApi, payments } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { LogOut, Crown } from "lucide-react";

export default function PayGate({ user, refreshUser, onLogout }) {
  const [plans, setPlans] = useState([]);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    plansApi.list().then(setPlans);
  }, []);

  const startCheckout = async (planId) => {
    setBuying(planId);
    try {
      const order = await payments.createOrder(planId);
      const cf = window.Cashfree({ mode: order.mode });
      await cf.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: "_modal",
      });
      // After modal closes
      setTimeout(async () => {
        try {
          const res = await payments.getOrder(order.order_id);
          if (res?.order?.status === "paid" || res?.cashfree_status === "PAID") {
            toast.success("Payment confirmed. Welcome to the club.");
          }
        } catch (_) {}
        refreshUser();
      }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setBuying(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden grain">
      <div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full float-slow"
        style={{
          background: "radial-gradient(circle, rgba(217,70,239,0.25), transparent 60%)",
          filter: "blur(60px)",
        }}
      />
      <div
        className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full float-slow"
        style={{
          background: "radial-gradient(circle, rgba(6,182,212,0.22), transparent 60%)",
          filter: "blur(60px)",
          animationDelay: "-4s",
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            {user.picture && (
              <img
                src={user.picture}
                alt=""
                className="w-10 h-10 rounded-full border border-zinc-800"
              />
            )}
            <div>
              <div className="font-heading font-bold">Hey {user.name?.split(" ")[0] || "DJ"}</div>
              <div className="text-xs text-zinc-500">{user.email}</div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={onLogout}
            className="border-zinc-800 text-zinc-400"
            data-testid="paygate-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>

        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-800 bg-zinc-950/50 mb-4">
            <Crown className="w-3 h-3 text-yellow-500" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">CHOOSE A PLAN TO CONTINUE</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl leading-[0.95] tracking-tighter">
            One last step. <br />
            <span
              style={{
                background: "linear-gradient(90deg,#d946ef,#06b6d4)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Unlock the decks.
            </span>
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plans.map((p) => {
            const isMonthly = p.id === "monthly_pass_499";
            const accent = isMonthly ? "#06b6d4" : "#d946ef";
            return (
              <div
                key={p.id}
                className="rounded-2xl border p-6"
                style={{
                  background: isMonthly
                    ? "linear-gradient(135deg, rgba(6,182,212,0.12), rgba(217,70,239,0.08))"
                    : "linear-gradient(135deg, rgba(217,70,239,0.10), transparent)",
                  borderColor: `${accent}55`,
                }}
                data-testid={`paygate-plan-${p.id}`}
              >
                <div className="font-mono text-[10px] tracking-widest text-zinc-400 mb-2">
                  {p.name.toUpperCase()}
                </div>
                <div className="font-heading font-black text-5xl mb-2" style={{ color: accent }}>
                  ₹{p.amount}
                </div>
                <div className="text-sm text-zinc-400 mb-6">{p.description}</div>
                <Button
                  onClick={() => startCheckout(p.id)}
                  disabled={buying === p.id}
                  className="w-full py-6 font-mono text-xs tracking-widest font-bold"
                  style={{
                    background: isMonthly ? accent : "#ffffff",
                    color: isMonthly ? "#050505" : "#050505",
                    boxShadow: `0 0 20px ${accent}44`,
                  }}
                  data-testid={`paygate-buy-${p.id}`}
                >
                  {buying === p.id ? "PROCESSING..." : `PAY ₹${p.amount} & PLAY`}
                </Button>
              </div>
            );
          })}
        </div>
        <div className="mt-6 text-center text-xs text-zinc-600 font-mono tracking-widest">
          POWERED BY CASHFREE · TEST MODE · USE ANY TEST CARD
        </div>
      </div>
    </div>
  );
}
